/*global google */

import { Loader } from '@googlemaps/js-api-loader';

import { formatPostalCode } from '@/helpers/string';

let loaded = false;
const endpoint = 'https://maps.googleapis.com/maps/api/geocode/json';

export const initGoogleAPILoader = () => {
  const loader = new Loader({
    apiKey: process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY,
    version: 'weekly'
  });
  loader.load().then(() => {
    loaded = true;
  });
};


export async function getCountryFromCoords({ lat, lng }) {
  const url = `${endpoint}?language=nl&latlng=${encodeURIComponent(
    `${lat},${lng}`
  )}&key=${process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY}`;

  try {
    const api = await fetch(url);
    if (!api.ok) return null;

    const res = await api.json();
    if (!res?.results?.length) return null;

    for (const result of res.results) {
      const countryComponent = result.address_components?.find(component =>
        component.types.includes('country')
      );

      if (countryComponent) {
        return {
          name: countryComponent.long_name,
          code: countryComponent.short_name,
        };
      }
    }

    return null;
  } catch (err) {
    console.error(err);
    return null;
  }
}



const componentsToAddressObject = (result, component) => {
  if (component.types.includes('administrative_area_level_1')) {
    result.region = component.long_name;
  } else if (component.types.includes('administrative_area_level_2')) {
    result.province = component.long_name;
  } else if (component.types.includes('country')) {
    result.country = component.long_name;
  } else if (component.types.includes('postal_code')) {
    result.postalCode = formatPostalCode(component.long_name);
  } else if (component.types.includes('route')) {
    result.street = component.long_name;
  }
  // This consists of number + suffix...
  else if (component.types.includes('street_number')) {
    result.street_number = component.long_name;
  } else if (component.types.includes('locality') || component.types.includes('postal_town')) {
    result.city = component.long_name;
  }

  return result;
};

/**
 * May be triggered in realtime based on user interaction
 *  https://developers.google.com/maps/documentation/javascript/geocoding#ReverseGeocoding
 */
export const dynamicReverseGeocode = async ({ lat, lng, bilingual = false }) => {
  const latlng = {
    lat: parseFloat(lat),
    lng: parseFloat(lng)
  };
  // console.log(lat, lng)
  const validTypes = ['street_address', 'premise', 'subpremise', 'administrative_area_level_2'];

  return new Promise((resolve, reject) => {
    if (!loaded) {
      reject('Geocoder is not (yet) available');
    } else {
      const geocoder = new google.maps.Geocoder();

      if (bilingual) { // Brussels e.g
        const dutchRequest = geocoder.geocode({ location: latlng, language: 'nl' });
        const frenchRequest = geocoder.geocode({ location: latlng, language: 'fr' });

        Promise.all([dutchRequest, frenchRequest]).then(results => {
          const dutchResult = results[0].results[0].address_components.reduce(componentsToAddressObject, {});
          const frenchResult = results[1].results[0].address_components.reduce(componentsToAddressObject, {})
          if (!results.length) {
            reject(status)
          }

          dutchResult.streetFr = frenchResult?.street
          dutchResult.cityFr = frenchResult?.city

          resolve(dutchResult)
        });
      } else {
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results.length !== 0) {

            results = results.filter(result => validTypes.some(type => result.types.includes(type)));

            // console.log(results.map(result => {
            //   return {
            //     address: result.formatted_address,
            //     lat: result.geometry.location.lat(),
            //     lng: result.geometry.location.lng()
            //   }
            // }))

            if (results.length !== 0) {
              resolve(results[0].address_components.reduce(componentsToAddressObject, {}));
            } else {
              reject('not found');
            }
          } else {
            reject(status);
          }
        });
      }


    }
  });
};

/**
 * May be triggered in realtime based on user interaction
 *  https://developers.google.com/maps/documentation/javascript/geocoding
 */
export const dynamicGeocode = async ({ address }) => {
  // console.log("dynGeo", address)

  const validTypes = ['street_address', 'premise', 'subpremise', 'route']; // route ??

  return new Promise((resolve, reject) => {
    if (!loaded) {
      reject('Geocoder is not (yet) available');
    } else {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: encodeURIComponent(address) }, (results, status) => {
        if (status === 'OK' && results.length !== 0) {
          // console.log(results)

          // Don't accept results that only indicate a general area
          results = results.filter(result => validTypes.some(type => result.types.includes(type)));
          if (results.length && results[0].geometry && results[0].geometry.location) {
            resolve({
              lng: results[0].geometry.location.lng(),
              lat: results[0].geometry.location.lat()
            });
          } else {
            reject('not found');
          }
        } else {
          reject(status);
        }
      });
    }
  });
};

/**
 * Intended for one time usage per location, not realtime based on user input
 *   https://developers.google.com/maps/documentation/geocoding/overview#before-you-begin
 */
export const staticGeocode = async ({ components, iteration }) => {
  let endpoint = 'https://maps.googleapis.com/maps/api/geocode/json';

  // Ensure iteration is a number above 0
  iteration = parseInt(iteration, 10);
  if (!Number.isInteger(iteration) || iteration < 1) {
    iteration = 1;
  }

  if (
    iteration === 1 &&
    (!Object.prototype.hasOwnProperty.call(components, 'streetSuffix') ||
      components.streetSuffix.trim() === '' ||
      parseInt(components.streetSuffix) == components.streetSuffix)
  ) {
    iteration = 2;
  }

  if (iteration > 4) {
    return {
      result: null,
      iteration
    };
  }

  // console.log('iteration', iteration)

  let componentSegment = '';
  let addressSegment = '';

  // Iteration 1: include suffix if not numeric.
  if (iteration === 1) {
    componentSegment = `&components=${encodeURIComponent(
      `country:NL|postal_code:${components.postalCode}|route:${components.street}`
    )}`;
    addressSegment = `&address=${encodeURIComponent(
      `${components.street} ${components.streetNumber} ${components.streetSuffix}`
    )}`;
  }

  // Iteration 2: exclude suffix
  if (iteration === 2) {
    componentSegment = `&components=${encodeURIComponent(
      `country:NL|postal_code:${components.postalCode}|route:${components.street}`
    )}`;
    addressSegment = `&address=${encodeURIComponent(`${components.street} ${components.streetNumber}`)}`;
  }

  // Iteration 3: ignore postalCode, use street + number
  if (iteration === 3) {
    componentSegment = `&components=${encodeURIComponent('country:NL')}`;
    addressSegment = `&address=${encodeURIComponent(
      `${components.street} ${components.streetNumber}, ${components.city}`
    )}`;
  }

  // Iteration 4: exclude number & suffix
  if (iteration === 4) {
    componentSegment = `&components=${encodeURIComponent(`country:NL|postal_code:${components.postalCode}`)}`;
    addressSegment = `&address=${encodeURIComponent(components.street)}`;
  }

  let response = await fetch(
    `${endpoint}?key=${process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY}&language=nl${addressSegment}${componentSegment}`
  );

  response = await response.json();

  // console.log(response)
  // console.log(response.results)

  if (iteration === 4 && response.status === 'ZERO_RESULTS') {
    return {
      result: null,
      iteration
    };
  } else if (iteration < 4 && response.status === 'ZERO_RESULTS') {
    return await staticGeocode({ components, iteration: iteration + 1 });
  } else if (response.status === 'OK' && response.results.length !== 0) {
    let validTypes = ['street_address', 'premise', 'subpremise'];

    if (iteration === 3) {
      validTypes.push('route');
    }

    let results = response.results.filter(result => result.types.some(type => validTypes.includes(type)));

    if (results.length !== 0) {
      return {
        iteration,
        result: results[0].geometry.location // = {lat, lng}
      };
    } else if (iteration < 4) {
      return await staticGeocode({ components, iteration: iteration + 1 });
    } else {
      return {
        result: null,
        iteration
      };
    }
  } else if (response.status === 'OK') {
    return {
      result: null,
      iteration
    };
  }

  // Not OK. Try again at a later time
  return {
    result: false,
    iteration
  };
};

// Mapbox returns a text_<lang> name for each requested language, the first one is used for display //
export const PLACE_NAME_LANGUAGES = ['nl', 'fr', 'de', 'en'];

/**
 * Mapbox language string with the preferred (display) language first, e.g. 'fr' => 'fr,nl,de,en'
 *  Pass the current UI language (this.$i18n.locale); no i18n import here, this file is also used by Netlify functions (Node)
 */
export const getMapboxLanguages = (preferred) => {
  const first = PLACE_NAME_LANGUAGES.includes(preferred) ? preferred : 'nl';
  return [first, ...PLACE_NAME_LANGUAGES.filter(lang => lang !== first)].join(',');
};

/**
 * All names of a Mapbox feature or context item (text, text_nl, text_fr, ...)
 */
export const getMapboxPlaceNames = (place) =>
  Object.keys(place || {}).filter(key => key === 'text' || key.startsWith('text_')).map(key => place[key]);

/**
 * Municipality name at the given coordinates in the UI language, plus all its language variants
 *  e.g. { name: 'Amblève', names: ['Amel', 'Amblève', 'Amel', 'Amel'] }
 *  Google returns only one name (e.g. "Amel"), so this is used to match municipalities by any official name
 */
export async function getPlaceNamesFromCoords({ lng, lat, locale }) {
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=place&language=${getMapboxLanguages(locale)}&access_token=${process.env.VUE_APP_MAPBOX_TOKEN}`;

  try {
    const api = await fetch(url);
    if (!api.ok) return { name: '', names: [] };

    const place = (await api.json())?.features?.[0];
    // text = first requested language, i.e. the UI language //
    return { name: place?.text || '', names: getMapboxPlaceNames(place) };
  } catch (err) {
    console.error(err);
    return { name: '', names: [] };
  }
}

export async function getCoordinatesFromAddress({ address, code }) {
  const requestAddress = Object.values(address).join(', ');
  const countries = {
    4: 'NL',
    5: 'BE',
    9: 'UK'
  }
  const country = countries[code?.length] || 'NL'

  const url = `${endpoint}?language=${country}&address=${encodeURIComponent(
    requestAddress
  )}&components=country:${country}&key=${process.env.VUE_APP_GOOGLE_PUBLIC_API_KEY}`;

  try {
    const api = await fetch(url);
    const res = api.ok && (await api.json());

    if (!res?.results?.length) return null;

    const result = res.results[0];

    const location = result.geometry.location;
    return [location.lng, location.lat];

  } catch (err) {
    console.log(err);
  }
}
